import { CustomersAuthService } from '@services/utils/auth-services/customers-auth.service';
import { JobsAuthService } from '@services/utils/auth-services/jobs-auth.service';
import { InvoicesAuthService } from '@services/utils/auth-services/invoices-auth.service';
import { EstimatesAuthService } from '@services/utils/auth-services/estimates-auth.service';
import { PaymentsAuthService } from '@services/utils/auth-services/payments-auth.service';
import { Component, EventEmitter, Input, OnChanges, Output, ElementRef, ViewChild, HostListener } from "@angular/core";
import { Router } from '@angular/router';
import { CustomersService, GlobalsService, SleepService, LookupService, HttpService, JobsService, StructureTypeService, EquipmentTrackingService, EmailService, PaymentProcessorService, CustomerPortalService } from "@services";
import { ICustomerModel, ICustomCustomerFieldModel, ICustomerDisplayListModel, ICustomerDisplayListInvoiceModel, IJobModel, IInvoiceModel, IPaymentModel, IEquipmentTrackingModel, StructureModel, StructureTypeModel, IEmailModel, ICreditCardPaymentTypeModel } from "@models";
import { CustomerEditLayoutComponent } from './customer-edit-layout/customer-edit-layout.component';
import { CustomerProjectProgressComponent } from './project-progress/customer-project-progress.component';
import { SlickConfirmDialogComponent, SlickConfirmDialogResults, SlickDialogComponent, SlickToastService } from '@slick-components';
import { AddressValidationDialogComponent } from '@shared-components/address-validation-dialog';
import { CustomerDocumentDialogsService } from '@components/customer-document-dialogs/customer-document-dialogs.service';
import { JobHistoryComponent } from './job-history/job-history.component';
import { InvoicesGridComponent } from '@app/invoices/invoices-page/invoices-grid/invoices-grid.component';
import { EstimatesGridComponent } from '@app/estimates/estimates-page/estimates-grid/estimates-grid.component';
import { PaymentsGridComponent } from '@app/payments/payments-page/payments-grid/payments-grid.component';

@Component({
    selector: "customer-edit",
    templateUrl: "customer-edit.component.html",
    styleUrls: ['customer-edit.component.scss'],
    providers: [CustomersAuthService, JobsAuthService, InvoicesAuthService, EstimatesAuthService, PaymentsAuthService, EquipmentTrackingService, PaymentProcessorService, CustomerPortalService]
})
export class CustomerEditComponent implements OnChanges {
    @Input() customerModel: ICustomerModel;
    @Input() isDialog: boolean = false;
    // Dialogs normally open straight to the edit form; set this to open on the summary dashboard instead
    @Input() openInSummary: boolean = false;
    @Input() initialDashboardTab: string = 'Overview';
    @Input() initialAccountingTab: string = 'Invoices';
    @Output() onCustomerSaved = new EventEmitter<ICustomerModel>();
    @Output() onCustomerCancel = new EventEmitter<void>();
    @Output() onCustomerLoaded: EventEmitter<ICustomerModel> = new EventEmitter<ICustomerModel>();
    @Output() editingCustomerChanged = new EventEmitter<boolean>();

    @ViewChild("customerEditLayoutRef")
    customerEditLayoutRef: CustomerEditLayoutComponent;

    @ViewChild("resetCustomerSignedStatusRef")
    resetCustomerSignedStatusRef: SlickConfirmDialogComponent;

    @ViewChild("systemLogsDialogRef")
    systemLogsDialogRef: SlickDialogComponent;

    @ViewChild("paymentInfoDialogRef")
    paymentInfoDialogRef: SlickDialogComponent;

    // The tab grids and the project card, refreshed after a record is saved in a dialog. Each only
    // exists while its tab is showing.
    @ViewChild("jobHistoryRef") jobHistoryRef: JobHistoryComponent;
    @ViewChild("invoicesGridRef") invoicesGridRef: InvoicesGridComponent;
    @ViewChild("estimatesGridRef") estimatesGridRef: EstimatesGridComponent;
    @ViewChild("paymentsGridRef") paymentsGridRef: PaymentsGridComponent;
    @ViewChild("projectProgressRef") projectProgressRef: CustomerProjectProgressComponent;

    @ViewChild("customerPageScroll")
    customerPageScrollRef: ElementRef<HTMLDivElement>;

    @ViewChild("addressValidationDialogRef")
    addressValidationDialogRef: AddressValidationDialogComponent;

    skeditPay: boolean = GlobalsService.company.useSkeditPay;
    customers: ICustomerModel[];
    visibleItems: any[];
    spinnerStatus: string;
    agreementspinnerStatus: string;
    portalInviteSpinnerStatus: string;
    tabKey: string;
    flagNonSignedServiceAgreement: boolean;
    useLathamOrdering = GlobalsService.company.useLathamOrdering;
    showAdditionalAddresses: boolean = GlobalsService.company.customerAdditionalAddresses;
    customCustomerFields: ICustomCustomerFieldModel[];
    usingCreditCards = GlobalsService.company.useCCUSAePay ||
        GlobalsService.company.useBambora ||
        GlobalsService.company.useCCZift ||
        GlobalsService.company.useCCPayTrace;

    tabIndex: number = 0;

    showOrenda: boolean = GlobalsService.company.useOrenda;
    // TEMPORARY: Readings & Dosages restricted to company 1073 for now.
    showReadingsAndDosages: boolean = GlobalsService.company.useReadingsAndDosages && GlobalsService.company.companyId === 1073;
    loadingOrendaJob: boolean = false;
    isEditingCustomer = false;
    dashboardTabKey = 'Overview';
    accountingTabKey = 'Invoices';
    documentsTabKey = 'Photos';
    assetsTabKey = 'Site Assets';
    communicationTabKey = 'Customer Text';
    openJobs = [];
    customerJobs: IJobModel[] = [];
    customerEmails: IEmailModel[] = [];
    selectedCustomerEmail: IEmailModel = null;
    loadingCustomerEmails = false;
    customerEmailsLoaded = false;
    outstandingInvoices = [];
    siteAssets: StructureModel[] = [];
    structureTypes: StructureTypeModel[] = [];
    selectedEquipmentJob: IJobModel = null;
    equipmentTrackingModels: IEquipmentTrackingModel[] = [];
    loadingEquipmentTracking = false;
    selectedOrendaJob: IJobModel = null;
    totalOutstandingBalance: number = 0;
    pastDueBalance: number = 0;
    openInvoiceCount: number = 0;
    lastServiceDate: Date = null;
    quickbooksLastSyncDate: Date = null;
    businessCentralLastSyncDate: Date = null;

    creditCards: ICreditCardPaymentTypeModel[] = [];

    // Header action menus. Only one is ever open, and a click anywhere else closes it.
    openMenu: 'create' | 'more' = null;

    systemLogsDialogOpen = false;
    paymentInfoDialogOpen = false;

    portalLinkSpinnerStatus: string;
    activitiesSpinnerStatus: string;

    // Set just before a save made from a tab other than the edit form, so the ngOnChanges the
    // host's re-bind triggers lands back on that tab instead of Overview.
    private restoreTabsAfterSave: { dashboard: string, communication?: string } = null;

    isQuickbooksEnabled = GlobalsService.isQuickbooksEnabled;
    isBusinessCentralEnabled = GlobalsService.isBusinessCentralEnabled;
    // Same gate the board's New Lead button uses
    canAddPipelineLead = GlobalsService.checkPermission("SalesPipeline", "Edit");
    canUseTextChat = GlobalsService.company.useTextChat && GlobalsService.checkPermission("textchat", "access");

    // Same three states the sync icons on the edit form use: linked, queued to link, or opted out.
    get quickbooksSyncState(): 'synced' | 'syncing' | 'off' {
        if (this.customerModel?.quickbooksId)
            return 'synced';
        return this.customerModel?.syncToQuickbooks ? 'syncing' : 'off';
    }

    get businessCentralSyncState(): 'synced' | 'syncing' | 'off' {
        const id = this.customerModel?.businessCentralId;
        // The API hands back Guid.Empty for a customer that was never sent over
        const hasId = !!id && id !== '0' && id !== '00000000-0000-0000-0000-000000000000';

        if (hasId && this.customerModel?.syncToBusinessCentral)
            return 'synced';
        return this.customerModel?.syncToBusinessCentral ? 'syncing' : 'off';
    }

    // Same rule as the edit form: price levels only show when the company has more than one
    get showPriceLevel(): boolean {
        return this.lookupService.getPriceLevels().length > 1;
    }

    get priceLevelName(): string {
        const priceLevelId = this.customerModel?.priceLevelId;
        if (!priceLevelId)
            return null;
        // Passing the id keeps an inactive level the customer is still on
        return this.lookupService.getPriceLevels(priceLevelId).find(x => x.priceLevelId === priceLevelId)?.name ?? null;
    }

    constructor(
        private customersService: CustomersService,
        private httpService: HttpService,
        private lookupService: LookupService,
        private jobsService: JobsService,
        private emailService: EmailService,
        private structureTypeService: StructureTypeService,
        private equipmentTrackingService: EquipmentTrackingService,
        private paymentProcessorService: PaymentProcessorService,
        private documentDialogs: CustomerDocumentDialogsService,
        private customerPortalService: CustomerPortalService,
        public customersAuthService: CustomersAuthService,
        public jobsAuthService: JobsAuthService,
        public invoicesAuthService: InvoicesAuthService,
        public estimatesAuthService: EstimatesAuthService,
        public paymentsAuthService: PaymentsAuthService,
        private slickToastService: SlickToastService,
        private router: Router
    ) {
        this.flagNonSignedServiceAgreement = GlobalsService.company.flagNonSignedServiceAgreement;
        this.customCustomerFields = this.lookupService.getCustomCustomerFields();
    }

    @HostListener('document:click')
    onDocumentClick() {
        this.openMenu = null;
    }

    toggleMenu(menu: 'create' | 'more', e: Event) {
        // Without this the document listener above fires on the same click and closes it again
        e.stopPropagation();
        this.openMenu = (this.openMenu === menu) ? null : menu;
    }

    ngOnChanges() {
        if (this.customerEditLayoutRef)
            this.customerEditLayoutRef.isSubmitted = false;

        this.tabIndex = 0;
        this.tabKey = null;
        this.dashboardTabKey = this.initialDashboardTab || 'Overview';
        this.accountingTabKey = this.initialAccountingTab || 'Invoices';
        this.documentsTabKey = 'Photos';
        this.assetsTabKey = 'Site Assets';
        this.communicationTabKey = 'Customer Text';

        if (this.restoreTabsAfterSave) {
            this.dashboardTabKey = this.restoreTabsAfterSave.dashboard;
            this.communicationTabKey = this.restoreTabsAfterSave.communication || this.communicationTabKey;
            this.restoreTabsAfterSave = null;
        }

        this.openJobs = this.customerModel?.openJobs || [];
        this.customerJobs = [];
        this.selectedOrendaJob = null;
        this.loadingOrendaJob = false;
        this.selectedEquipmentJob = null;
        this.equipmentTrackingModels = [];
        this.loadingEquipmentTracking = false;

        // Dialogs and new customers should open the edit form, not the summary dashboard
        const isDialogMode = this.isDialog === true || (this.isDialog as any) === 'true';
        this.isEditingCustomer = (isDialogMode && !this.openInSummary) || !this.customerModel?.customerId;
        this.editingCustomerChanged.emit(this.isEditingCustomer);

        this.loadDashboardData();
    }

    async onTabChanged(tabKey: string) {
        this.tabKey = tabKey;
    }

    clearSubmitted() {
        if (this.customerEditLayoutRef)
            this.customerEditLayoutRef.isSubmitted = false;
    }

    setSubmitted() {
        if (this.customerEditLayoutRef)
            this.customerEditLayoutRef.isSubmitted = true;
    }

    isValid(): boolean {
        let isValid = true;
        if (this.customerEditLayoutRef && this.customerEditLayoutRef.isValid() === false)
            isValid = false;

        return isValid;
    }

    async saveCustomer(): Promise<ICustomerModel | null> {
        this.setSubmitted();
        if (this.isValid() === false) {
            this.spinnerStatus = "error";
            return null;
        }

        try {
            this.spinnerStatus = "spin";
            await SleepService.sleep(500);

            if (this.customerModel.linkBillingAddress) {
                this.customerModel.billingAddress1 = this.customerModel.address1;
                this.customerModel.billingAddress2 = this.customerModel.address2;
                this.customerModel.billingCity = this.customerModel.city;
                this.customerModel.billingState = this.customerModel.state;
                this.customerModel.billingCountry = this.customerModel.country;
                this.customerModel.billingZipcode = this.customerModel.zipcode;
            }

            if (this.customerModel.linkShippingAddress) {
                this.customerModel.shippingAddress1 = this.customerModel.address1;
                this.customerModel.shippingAddress2 = this.customerModel.address2;
                this.customerModel.shippingCity = this.customerModel.city;
                this.customerModel.shippingState = this.customerModel.state;
                this.customerModel.shippingCountry = this.customerModel.country;
                this.customerModel.shippingZipcode = this.customerModel.zipcode;
            }

            // Validate after the link copy above, so we check the shipping address that actually gets saved
            const canSave = await this.validateShippingAddress();

            if (canSave === false) {
                this.spinnerStatus = "reset";
                return null;
            }

            if (this.customerModel.discounts)
                this.customerModel.discounts = this.customerModel.discounts.filter(x => x.discountId !== 0);

            if (this.customerModel.rebates)
                this.customerModel.rebates = this.customerModel.rebates.filter(x => x.rebateId !== 0);

            if (!this.customerModel.customerId) {
                this.customerEditLayoutRef?.cancelPendingDuplicateCheck();
                this.customerModel = await this.customersService.addCustomer(this.customerModel);

                if (GlobalsService.company.autoSendServiceAgreement && this.customerModel.email) {
                    await this.customersService.sendServiceAgreement(this.customerModel.customerId);
                }
            }
            else
                this.customerModel = await this.customersService.updateCustomer(this.customerModel);

            this.onCustomerSaved.emit(this.customerModel);
            this.isEditingCustomer = false;
            this.editingCustomerChanged.emit(false);
            this.spinnerStatus = "ok";
            return this.customerModel;
        } catch (err) {
            this.spinnerStatus = "error";
            return null;
        }
    }

    // Returns false when the user wants to go back and fix the shipping address.
    private async validateShippingAddress(): Promise<boolean> {
        const validationResult = await this.addressValidationDialogRef.validate({
            address1: this.customerModel.shippingAddress1,
            address2: this.customerModel.shippingAddress2,
            city: this.customerModel.shippingCity,
            state: this.customerModel.shippingState,
            zipcode: this.customerModel.shippingZipcode
        });

        if (validationResult.proceed === false)
            return false;

        if (validationResult.useSuggested) {
            const validation = validationResult.validation;

            this.customerModel.shippingAddress1 = validation.address1;
            this.customerModel.shippingAddress2 = validation.address2 || this.customerModel.shippingAddress2;
            this.customerModel.shippingCity = validation.city;
            this.customerModel.shippingState = validation.state;
            this.customerModel.shippingZipcode = validation.zipcode;

            // When the addresses are linked, the main address is copied over shipping on every save,
            // so standardizing shipping alone would be undone the next time this customer is saved.
            if (this.customerModel.linkShippingAddress) {
                this.customerModel.address1 = validation.address1;
                this.customerModel.address2 = validation.address2 || this.customerModel.address2;
                this.customerModel.city = validation.city;
                this.customerModel.state = validation.state;
                this.customerModel.zipcode = validation.zipcode;
            }
        }

        return true;
    }

    async resetCustomerSignedStatus() {
        let reset = await this.resetCustomerSignedStatusRef.confirm();
        if (reset === SlickConfirmDialogResults.Ok) {
            await this.httpService.post(`/customers/resetCustomerSignedStatus?customerId=${this.customerModel.customerId}`, null);
            this.customerModel.acceptedTerms = false;
            this.slickToastService.showSuccess("Customer Signed Status Reset");
        }
    }

    async sendServiceAgreement() {
        this.agreementspinnerStatus = "spin";
        await SleepService.sleep(500);

        try {
            const sent = await this.customersService.sendServiceAgreement(this.customerModel.customerId);
            this.agreementspinnerStatus = sent ? "ok" : "error";

            // The More menu closes on click, so the button spinner isn't visible from there
            if (sent)
                this.slickToastService.showSuccess("Service agreement sent");
            else
                this.slickToastService.showWarning("The service agreement could not be sent");
        } catch (err) {
            this.agreementspinnerStatus = "error";
        }
    }

    async sendPortalInviteLink() {
        this.portalInviteSpinnerStatus = "spin";
        await SleepService.sleep(500);

        try {
            await this.customersService.sendPortalInviteLink(this.customerModel.customerId);
            this.portalInviteSpinnerStatus = "ok";
            this.slickToastService.showSuccess("Portal invite sent");
        } catch (err) {
            this.portalInviteSpinnerStatus = "error";
        }
    }

    async openCustomerPortal() {
        // Open the tab right away, while this is still a click - browsers block tabs opened after an await
        const portalTab = window.open("", "_blank");
        if (!portalTab) {
            this.slickToastService.showDanger("Your browser blocked the customer portal tab. Allow pop-ups for this site and try again.");
            return;
        }

        try {
            const result = await this.customersService.getPortalAccessLinkCode(this.customerModel.customerId);

            // The portal only opens for a linkCode its own tab was issued, so mark it in the new tab
            this.customerPortalService.markSessionAuthenticated(result.linkCode, portalTab.sessionStorage);
            portalTab.location.href = `${window.location.href.split('#')[0]}#/customerPortal/${result.linkCode}`;
        } catch (err) {
            portalTab.close();
            this.slickToastService.showDanger("Unable to open the customer portal");
        }
    }

    cancelCustomer() {
        this.customerModel = null;
        this.onCustomerCancel.emit();
    }

    editCustomer() {
        this.isEditingCustomer = true;
        this.editingCustomerChanged.emit(true);
    }

    cancelEditCustomer() {
        this.isEditingCustomer = false;
        this.editingCustomerChanged.emit(false);
    }

    setDashboardTab(tabKey: string) {
        this.dashboardTabKey = tabKey;

        setTimeout(() => {
            if (this.customerPageScrollRef) {
                this.customerPageScrollRef.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    // ---- Opening records ---------------------------------------------------
    // Everything opens in a dialog over the page (the same dialogs the pipeline's ticket detail
    // uses), so the user never loses their place on the customer. The dialogs are mounted by the
    // customers page; if this dashboard is ever rendered somewhere that didn't mount them, the
    // navigation fallback keeps the links working.

    async openJob(job: IJobModel | number) {
        const jobId = typeof job === 'number' ? job : job?.jobId;
        if (!jobId || !this.customerModel?.customerId)
            return;

        if (!this.documentDialogs.isAvailable) {
            this.router.navigate(['/jobs'], {
                queryParams: { jobId, returnCustomerId: this.customerModel.customerId, returnDashboardTab: 'Jobs' }
            });
            return;
        }

        const saved = await this.documentDialogs.openJob(job);
        if (saved)
            await this.afterJobSaved(saved);
    }

    async openEstimate(estimate: IInvoiceModel | number) {
        const invoiceId = typeof estimate === 'number' ? estimate : estimate?.invoiceId;
        if (!invoiceId || !this.customerModel?.customerId)
            return;

        if (!this.documentDialogs.isAvailable) {
            this.router.navigate(['/estimates'], {
                queryParams: {
                    estimateId: invoiceId,
                    returnCustomerId: this.customerModel.customerId,
                    returnDashboardTab: 'Accounting',
                    returnAccountingTab: 'Estimates'
                }
            });
            return;
        }

        const saved = await this.documentDialogs.openQuote(estimate);
        if (saved)
            await this.afterInvoiceSaved(saved);
    }

    async openInvoice(invoice: IInvoiceModel | number) {
        const invoiceId = typeof invoice === 'number' ? invoice : invoice?.invoiceId;
        if (!invoiceId || !this.customerModel?.customerId)
            return;

        if (!this.documentDialogs.isAvailable) {
            this.router.navigate(['/invoices'], {
                queryParams: {
                    invoiceId,
                    customerId: this.customerModel.customerId,
                    returnCustomerId: this.customerModel.customerId,
                    returnDashboardTab: 'Accounting',
                    returnAccountingTab: 'Invoices'
                }
            });
            return;
        }

        const saved = await this.documentDialogs.openInvoice(invoice);
        if (saved)
            await this.afterInvoiceSaved(saved);
    }

    async openPayment(payment: IPaymentModel | number) {
        const paymentId = typeof payment === 'number' ? payment : payment?.paymentId;
        if (!paymentId || !this.customerModel?.customerId)
            return;

        if (!this.documentDialogs.isAvailable) {
            this.router.navigate(['/payments'], { queryParams: { paymentId } });
            return;
        }

        const saved = await this.documentDialogs.openPayment(payment);
        if (saved)
            await this.afterPaymentSaved(saved);
    }

    async openLathamOrder(lathamOrderId: number) {
        if (!lathamOrderId)
            return;

        if (!this.documentDialogs.isAvailable) {
            this.router.navigate(['/latham-orders'], { queryParams: { lathamOrderId } });
            return;
        }

        await this.documentDialogs.openLathamOrder(lathamOrderId);
    }

    // A record saved in a dialog changes the numbers up top and the row in whichever grid is
    // showing, so both get refreshed. The grids only exist while their tab is open.
    // The Jobs tab is the appointment history rather than a grid, so it reloads as a whole; an invoice
    // save reloads it too, since every card carries its job's invoice number and total.
    private async afterJobSaved(job: IJobModel) {
        await this.jobHistoryRef?.refreshHistory();
        await this.refreshAfterDocumentChange();
    }

    private async afterInvoiceSaved(invoice: IInvoiceModel) {
        if (invoice.isQuote)
            this.estimatesGridRef?.refreshRow(invoice);
        else
            this.invoicesGridRef?.refreshRow(invoice);

        await this.jobHistoryRef?.refreshHistory();
        await this.refreshAfterDocumentChange();
    }

    private async afterPaymentSaved(payment: IPaymentModel) {
        this.paymentsGridRef?.refreshRow(payment);
        await this.refreshAfterDocumentChange();
    }

    private async refreshAfterDocumentChange() {
        await this.loadDashboardData();
        this.projectProgressRef?.reload();
    }

    async existingCustomerSelected(customerId: number) {
        this.customerModel = await this.customersService.getCustomer(customerId);

        const dashboardData = await this.customersService.getCustomerDisplayListModel(customerId);
        this.onCustomerLoaded.emit(this.customerModel);
        this.applyDashboardData(dashboardData);

        await this.loadCreditCards();

        this.customerJobs = [];
        this.selectedOrendaJob = null;
        this.loadingOrendaJob = false;

        this.selectedEquipmentJob = null;
        this.equipmentTrackingModels = [];
        this.loadingEquipmentTracking = false;

        this.structureTypes =
            await this.structureTypeService.getStructureTypes();

        this.siteAssets =
            await this.structureTypeService.getStructures(customerId);
    }

    async loadDashboardData() {
        if (!this.customerModel?.customerId) {
            this.openJobs = [];
            this.outstandingInvoices = [];
            this.siteAssets = [];
            this.structureTypes = [];
            this.creditCards = [];
            this.lastServiceDate = null;
            this.quickbooksLastSyncDate = null;
            this.businessCentralLastSyncDate = null;
            return;
        }

        const dashboardData =
            await this.customersService.getCustomerDisplayListModel(
                this.customerModel.customerId
            );

        this.applyDashboardData(dashboardData);

        this.structureTypes =
            await this.structureTypeService.getStructureTypes();

        this.siteAssets =
            await this.structureTypeService.getStructures(
                this.customerModel.customerId
            );

        await this.loadCreditCards();
    }

    private applyDashboardData(dashboardData: ICustomerDisplayListModel) {
        this.openJobs = dashboardData.openJobs || [];
        this.outstandingInvoices = dashboardData.outstandingInvoices || [];
        this.totalOutstandingBalance = dashboardData.totalOutstandingBalance || 0;
        this.pastDueBalance = dashboardData.pastDueBalance || 0;
        this.openInvoiceCount = dashboardData.openInvoiceCount || 0;
        this.lastServiceDate = dashboardData.lastServiceDate || null;
        this.quickbooksLastSyncDate = dashboardData.quickbooksLastSyncDate || null;
        this.businessCentralLastSyncDate = dashboardData.businessCentralLastSyncDate || null;
    }

    private async loadCreditCards() {
        this.creditCards = [];

        // Nothing to ask for when the company has no processor hooked up - PaymentProcessorService
        // has no processor to delegate to in that case.
        if (!this.usingCreditCards || !this.customerModel?.customerId)
            return;

        try {
            this.creditCards = await this.paymentProcessorService.getCreditCardPaymentTypeForCustomer(this.customerModel.customerId) || [];
        }
        catch {
            this.creditCards = [];
        }
    }

    get defaultCreditCard(): ICreditCardPaymentTypeModel {
        if (!this.creditCards || this.creditCards.length === 0)
            return null;

        return this.creditCards.find(x => x.isDefault) || this.creditCards[0];
    }

    // Font Awesome brand glyph for the card on file, matching the Payment Info form's detection
    get creditCardBrandClass(): string {
        const paymentType = (this.defaultCreditCard?.paymentType || '').toLowerCase();

        if (paymentType.indexOf('visa') > -1)
            return 'fab fa-cc-visa brand-visa';
        if (paymentType.indexOf('mastercard') > -1)
            return 'fab fa-cc-mastercard brand-mastercard';
        if (paymentType.indexOf('discover') > -1)
            return 'fab fa-cc-discover brand-discover';
        if (paymentType.indexOf('american express') > -1 || paymentType.indexOf('amex') > -1)
            return 'fab fa-cc-amex brand-amex';

        return 'bi bi-credit-card-2-front-fill brand-generic';
    }

    setAccountingTab(tabKey: string) {
        this.accountingTabKey = tabKey;

        setTimeout(() => {
            if (this.customerPageScrollRef) {
                this.customerPageScrollRef.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    async setAssetsTab(tabKey: string) {
        this.assetsTabKey = tabKey;

        if (tabKey === 'Equipment Tracking' && !this.selectedEquipmentJob) {
            await this.loadInitialEquipmentJob();
        }

        if (tabKey === 'Orenda' && !this.selectedOrendaJob) {
            await this.loadInitialOrendaJob();
        }

        setTimeout(() => {
            if (this.customerPageScrollRef) {
                this.customerPageScrollRef.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    async setCommunicationTab(tabKey: string) {
        this.communicationTabKey = tabKey;

        if (tabKey === 'Emails' && !this.customerEmailsLoaded) {
            await this.loadCustomerEmails();
        }

        setTimeout(() => {
            if (this.customerPageScrollRef) {
                this.customerPageScrollRef.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    async loadCustomerEmails() {
        if (!this.customerModel?.customerId) {
            this.customerEmails = [];
            this.selectedCustomerEmail = null;
            return;
        }

        try {
            this.loadingCustomerEmails = true;

            this.customerEmails = await this.emailService.getAllEmails(
                0,
                200,
                '',
                this.customerModel.customerId
            );

            this.selectedCustomerEmail =
                this.customerEmails.length > 0 ? this.customerEmails[0] : null;

            this.customerEmailsLoaded = true;
        }
        finally {
            this.loadingCustomerEmails = false;
        }
    }

    showCustomerInvoices() {
        this.accountingTabKey = 'Invoices';
        this.setDashboardTab('Accounting');
    }

    isPastDue(invoice: ICustomerDisplayListInvoiceModel): boolean {
        return !!invoice.invoiceDueDate && new Date(invoice.invoiceDueDate) < new Date();
    }

    // Big balances get smaller type in the indicator cards so they never clip and five cards
    // still share a row. Thresholds are on the formatted length: "$100,000.00" is 11 chars.
    amountSizeClass(amount: number): string {
        const digits = Math.abs(Math.trunc(amount || 0)).toString().length;

        if (digits >= 8)
            return 'summary-value-xlong';
        if (digits >= 6)
            return 'summary-value-long';

        return '';
    }

    openSystemLogs() {
        this.systemLogsDialogOpen = true;
        this.systemLogsDialogRef.showDialog();
    }

    openPaymentInfo() {
        this.paymentInfoDialogOpen = true;
        this.paymentInfoDialogRef.showDialog();
    }

    // Cards may have been added or removed while the dialog was open
    async onPaymentInfoClosed() {
        this.paymentInfoDialogOpen = false;
        await this.loadCreditCards();
    }

    // ---- CRM activities ----------------------------------------------------
    // The crm-edit panel edits outreach entries and the next-outreach schedule straight on the
    // customer model, exactly as the old CRM tab did, so saving is the ordinary customer save.

    async saveActivities() {
        if (!this.customersAuthService.canEdit())
            return;

        // The host re-binds the saved customer, which runs ngOnChanges and would drop back to the
        // Overview tab; keep the user where they were.
        this.restoreTabsAfterSave = { dashboard: 'Communication', communication: 'Activities' };

        this.activitiesSpinnerStatus = 'spin';
        const saved = await this.saveCustomer();
        this.activitiesSpinnerStatus = saved ? 'ok' : 'error';

        if (saved)
            this.slickToastService.showSuccess('CRM activities saved');
        else
            this.restoreTabsAfterSave = null;
    }

    async discardActivities() {
        if (!this.customerModel?.customerId)
            return;

        // Re-read the customer so unsaved outreach edits fall away; the binding into crm-edit picks
        // up the fresh model.
        this.customerModel = await this.customersService.getCustomer(this.customerModel.customerId);
    }

    openTextChat() {
        this.communicationTabKey = 'Customer Text';
        this.setDashboardTab('Communication');
    }

    // ---- Create menu -------------------------------------------------------
    // Each opens the record's editor as a dialog over the page, seeded with this customer. The
    // optional ticket is the project the Project Progress card was showing, so the new document
    // lands on that project rather than whichever one the link rules would pick.

    async createJob(preferredSalesTicketId?: number) {
        if (!this.jobsAuthService.canAdd() || !this.customerModel?.customerId)
            return;

        const saved = await this.documentDialogs.newJob(this.customerModel, preferredSalesTicketId);
        if (saved)
            await this.afterJobSaved(saved);
    }

    async createInvoice(preferredSalesTicketId?: number) {
        if (!this.invoicesAuthService.canAdd() || !this.customerModel?.customerId)
            return;

        const saved = await this.documentDialogs.newInvoice(this.customerModel, preferredSalesTicketId);
        if (saved)
            await this.afterInvoiceSaved(saved);
    }

    async createQuote(preferredSalesTicketId?: number) {
        if (!this.estimatesAuthService.canAdd() || !this.customerModel?.customerId)
            return;

        const saved = await this.documentDialogs.newQuote(this.customerModel, preferredSalesTicketId);
        if (saved)
            await this.afterInvoiceSaved(saved);
    }

    async createPayment() {
        if (!this.paymentsAuthService.canAdd() || !this.customerModel?.customerId)
            return;

        const saved = await this.documentDialogs.newPayment(this.customerModel);
        if (saved)
            await this.afterPaymentSaved(saved);
    }

    // Puts the customer on the Sales Pipeline as a new lead, through the board's own ticket form.
    // The saved ticket is an open project, so the Project Progress card picks it up straight away.
    async createLead() {
        if (!this.canAddPipelineLead || !this.customerModel?.customerId)
            return;

        if (!this.documentDialogs.isAvailable) {
            this.router.navigate(['/sales-pipeline']);
            return;
        }

        const saved = await this.documentDialogs.newLead(this.customerModel);
        if (saved) {
            this.slickToastService.showSuccess(`${saved.ticketNumber} added to the pipeline`);
            this.projectProgressRef?.reload();
        }
    }

    // ---- More menu ---------------------------------------------------------

    async sendCustomerPortalLink() {
        if (!this.customerModel?.email) {
            this.slickToastService.showWarning("This customer has no email address");
            return;
        }

        this.portalLinkSpinnerStatus = "spin";
        await SleepService.sleep(500);

        try {
            const sent = await this.customersService.sendPortalInviteLink(this.customerModel.customerId);
            this.portalLinkSpinnerStatus = sent ? "ok" : "error";

            if (sent)
                this.slickToastService.showSuccess("Customer portal link sent");
            else
                this.slickToastService.showWarning("The portal link could not be sent");
        }
        catch {
            this.portalLinkSpinnerStatus = "error";
        }
    }

    getStructureTypeName(structureTypeId: number): string {
        const structureType =
            this.structureTypes.find(x => x.id === structureTypeId);

        return structureType?.name || 'Site Asset';
    }

    async loadInitialEquipmentJob() {
        if (!this.openJobs?.length) {
            this.selectedEquipmentJob = null;
            this.equipmentTrackingModels = [];
            return;
        }

        await this.selectEquipmentJob(this.openJobs[0]);
    }

    async selectEquipmentJob(job: IJobModel) {
        if (!job?.jobId) {
            this.selectedEquipmentJob = null;
            this.equipmentTrackingModels = [];
            return;
        }

        try {
            this.loadingEquipmentTracking = true;
            this.equipmentTrackingModels = [];

            // Load the complete job so documentKey is available.
            this.selectedEquipmentJob =
                await this.jobsService.getJob(job.jobId);

            this.equipmentTrackingModels =
                await this.equipmentTrackingService.getEquipmentTrackings(
                    this.selectedEquipmentJob.jobId
                );
        }
        finally {
            this.loadingEquipmentTracking = false;
        }
    }

    async selectEquipmentJobById(jobId: number) {
        const job = this.openJobs.find(x => x.jobId === Number(jobId));

        if (job) {
            await this.selectEquipmentJob(job);
        }
    }

    async loadInitialOrendaJob() {
        if (!this.openJobs?.length) {
            this.selectedOrendaJob = null;
            return;
        }

        await this.selectOrendaJob(this.openJobs[0]);
    }

    async selectOrendaJob(job: IJobModel) {
        if (!job?.jobId) {
            this.selectedOrendaJob = null;
            return;
        }

        try {
            this.loadingOrendaJob = true;

            // Load the complete job so documentKey is available.
            this.selectedOrendaJob =
                await this.jobsService.getJob(job.jobId);
        }
        finally {
            this.loadingOrendaJob = false;
        }
    }

    async selectOrendaJobById(jobId: number) {
        const job = this.openJobs.find(
            x => x.jobId === Number(jobId)
        );

        if (job) {
            await this.selectOrendaJob(job);
        }
    }
}